"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { ScrollReveal } from "@/app/ScrollReveal";
import { SiteFooter } from "@/app/SiteFooter";
import { SiteHeader } from "@/app/SiteHeader";
import type { LocaleContext } from "@/lib/i18n";
import { localizeHref } from "@/lib/i18n";
import { getMessages } from "@/lib/messages";

export function HelpContent({ locale, country }: LocaleContext) {
  const m = getMessages(locale);
  const ctx = { locale, country };
  const [query, setQuery] = useState("");

  const sections = useMemo(
    () => [
      {
        id: "apps",
        title: m.help.sectionAppsTitle,
        description: m.help.sectionAppsDesc,
        items: m.help.sectionAppsItems,
      },
      {
        id: "web-support",
        title: m.help.sectionSupportTitle,
        description: m.help.sectionSupportDesc,
        items: m.help.sectionSupportItems,
      },
      {
        id: "hosting",
        title: m.help.sectionHostingTitle,
        description: m.help.sectionHostingDesc,
        items: m.help.sectionHostingItems,
      },
    ],
    [m]
  );

  const normalizedQuery = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!normalizedQuery) return sections;
    return sections
      .map((section) => {
        const haystack = (
          section.title +
          " " +
          section.description +
          " " +
          section.items.join(" ")
        ).toLowerCase();
        if (!haystack.includes(normalizedQuery)) {
          return null;
        }
        return section;
      })
      .filter(Boolean) as typeof sections;
  }, [normalizedQuery, sections]);

  const hasResults = filtered.length > 0;

  return (
    <div className="min-h-screen">
      <SiteHeader locale={locale} country={country} subtitle={m.help.subtitle} />

      <main id="main-content" className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-24 pt-8">
        <ScrollReveal>
          <div className="eyebrow text-cyan-700/80 dark:text-cyan-300/80">{m.help.eyebrow}</div>
          <h1 className="font-display mt-3 text-4xl italic tracking-tight text-[#0f172b] md:text-5xl dark:text-white mask-rise">
            {m.help.title}
          </h1>
          <p className="mt-4 max-w-2xl rounded-2xl bg-white/45 px-4 py-3 text-base text-slate-600 ring-1 ring-white/35 backdrop-blur-xl dark:bg-white/10 dark:text-white/70 dark:ring-white/15">
            {m.help.introBefore}{" "}
            <Link
              href={localizeHref("/contact", ctx)}
              className="underline underline-offset-4 hover:text-slate-900 dark:hover:text-white"
            >
              {m.help.contactLink}
            </Link>
            {m.help.introAfter}
          </p>
        </ScrollReveal>

        <section className="mt-10">
          <ScrollReveal>
            <div className="service-card premium-border relative overflow-hidden rounded-[2rem] bg-white/40 p-5 ring-1 ring-white/30 backdrop-blur-2xl dark:bg-white/10 dark:ring-white/20">
              <div className="absolute inset-0 bg-gradient-to-br from-white/25 via-transparent to-transparent dark:from-white/10 dark:via-transparent" />
              <div className="relative">
                <label htmlFor="help-search" className="eyebrow text-slate-500 dark:text-white/55">
                  {m.help.searchLabel}
                </label>
                <div className="mt-3 flex items-center gap-2 rounded-2xl bg-white/50 px-3 py-2 ring-1 ring-white/40 backdrop-blur-xl dark:bg-black/40 dark:ring-white/15">
                  <svg
                    className="h-4 w-4 text-slate-400 dark:text-white/50"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <path
                      d="M15.5 15.5L20 20"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="11" cy="11" r="5.25" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                  <input
                    id="help-search"
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={m.help.searchPlaceholder}
                    className="h-9 w-full bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-white dark:placeholder:text-white/50"
                  />
                </div>
                {normalizedQuery && (
                  <p className="mt-2 text-xs text-slate-500 dark:text-white/60">
                    {hasResults ? m.help.filtering : m.help.noMatches}
                  </p>
                )}
              </div>
            </div>
          </ScrollReveal>
        </section>

        <div className="hairline mt-16" />

        <section className="mt-12">
          <ScrollReveal>
            <div className="eyebrow text-cyan-700/80 dark:text-cyan-300/80">{m.help.browseEyebrow}</div>
            <h2 className="font-display mt-3 text-3xl tracking-tight text-slate-900 md:text-4xl dark:text-white">
              {m.help.quickHelp}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-white/70">{m.help.browseIntro}</p>
          </ScrollReveal>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {sections.map((section, i) => (
              <ScrollReveal key={section.id} delay={i * 100}>
                <div className="service-card premium-border relative overflow-hidden rounded-[2rem] bg-white/40 p-6 ring-1 ring-white/30 backdrop-blur-2xl dark:bg-white/10 dark:ring-white/20">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/25 via-transparent to-transparent dark:from-white/10 dark:via-transparent" />
                  <div className="relative">
                    <div className="flex items-baseline justify-between">
                      <div className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
                        {section.title}
                      </div>
                      <div className="font-stat text-2xl font-medium text-slate-300/80 dark:text-white/20">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-white/70">
                      {section.description}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </section>

        <div className="hairline mt-16" />

        <section className="mt-12 space-y-6">
          {filtered.map((section, i) => (
            <ScrollReveal key={section.id} delay={i * 60}>
              <article
                id={section.id}
                className="service-card premium-border relative overflow-hidden rounded-[2rem] bg-white/40 p-6 ring-1 ring-white/30 backdrop-blur-2xl dark:bg-white/10 dark:ring-white/20"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/25 via-transparent to-transparent dark:from-white/10 dark:via-transparent" />
                <div className="relative">
                  <h2 className="font-display text-2xl tracking-tight text-slate-900 dark:text-white">
                    {section.title}
                  </h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-white/70">{section.description}</p>
                  <ul className="mt-4 space-y-2.5 text-sm text-slate-600 dark:text-white/75">
                    {section.items.map((item) => (
                      <li key={item} className="flex gap-2.5">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-500/70 dark:bg-cyan-300/80" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </ScrollReveal>
          ))}
        </section>

        <SiteFooter />
      </main>
    </div>
  );
}
